import "@/App.css";
import { useState, useEffect } from "react";
import { MapPin, Phone, Mail, Menu, X, ChevronDown, Target, FileText, Building, Grid3X3, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/d1bbc1b7-f390-4051-9b1b-f216f82c29f2/images/365209b28c66c8bd576290f7e673fadac4e05f3e08b25566c12e9456a1d8a1d6.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1562601622-e3ea198a61e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxzdXJ2ZXlvciUyMHRvdGFsJTIwc3RhdGlvbiUyMHRoZW9kb2xpdGV8ZW58MHx8fHwxNzc1NDY3MjYxfDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMAGE = "https://images.unsplash.com/photo-1562601622-d344391156fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxzdXJ2ZXlvciUyMHRvdGFsJTIwc3RhdGlvbiUyMHRoZW9kb2xpdGV8ZW58MHx8fHwxNzc1NDY3MjYxfDA&ixlib=rb-4.1.0&q=85";
const SERVICE_IMG_1 = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50cyUyMGRlc2t8ZW58MHx8fHwxNzc1NDY3MjYxfDA&ixlib=rb-4.1.0&q=85";
const SERVICE_IMG_2 = "https://images.unsplash.com/photo-1706333593422-4162aa2e487d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZHJvbmUlMjBhZXJpYWx8ZW58MHx8fHwxNzc1NDY3MjYxfDA&ixlib=rb-4.1.0&q=85";
const PORTFOLIO_1 = "https://images.unsplash.com/photo-1687079661575-94c6490e049e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZHJvbmUlMjBhZXJpYWx8ZW58MHx8fHwxNzc1NDY3MjYxfDA&ixlib=rb-4.1.0&q=85";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Início", href: "#inicio" },
    { label: "Sobre Nós", href: "#sobre" },
    { label: "Serviços", href: "#servicos" },
    { label: "Projetos", href: "#projetos" },
    { label: "Contactos", href: "#contactos" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#FDFDF9]/90 backdrop-blur-xl shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <a href="#inicio" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="RPC Topografia" className="h-12 w-auto" />
            <span className={`font-bold text-lg tracking-tight ${isScrolled ? "text-[#0A3D62]" : "text-[#FDFDF9]"}`}>
              RPC Topografia
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`nav-link text-sm font-medium ${
                  isScrolled ? "text-[#2E2E2E]" : "text-[#FDFDF9]"
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? "text-[#2E2E2E]" : "text-[#FDFDF9]"} size={24} />
            ) : (
              <Menu className={isScrolled ? "text-[#2E2E2E]" : "text-[#FDFDF9]"} size={24} />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#FDFDF9] border-t border-[#0A3D62]/10" data-testid="mobile-menu">
          <nav className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-[#2E2E2E] text-base font-medium py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

const HeroSection = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-start"
      style={{
        backgroundImage: `url(${HERO_IMAGE})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 hero-gradient" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-32">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CAB5A] mb-6 animate-fade-in-up">
            Topografia Profissional em Lisboa
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#FDFDF9] mb-6 animate-fade-in-up animate-delay-100">
            Precisão que constrói confiança.
          </h1>
          <p className="text-lg sm:text-xl text-[#FDFDF9]/90 leading-relaxed mb-10 max-w-2xl animate-fade-in-up animate-delay-200">
            Levantamentos topográficos, loteamentos, implantação de obras e cadastro predial. 
            Serviços técnicos de excelência para os seus projetos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-300">
            <a
              href="#servicos"
              className="cta-button px-8 py-4 rounded-sm text-base font-semibold text-center"
              data-testid="hero-cta-secondary"
            >
              Ver Serviços
            </a>
          </div>
        </div>
      </div>

      <a
        href="#sobre"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#FDFDF9] animate-bounce"
        data-testid="scroll-indicator"
      >
        <ChevronDown size={32} />
      </a>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="sobre" data-testid="about-section" className="section-padding bg-[#FDFDF9]">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CAB5A] mb-4">
              Sobre Nós
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A3D62] mb-6">
              Experiência e dedicação ao serviço da topografia
            </h2>
            <div className="space-y-4 text-base sm:text-lg leading-relaxed text-[#2E2E2E]/80">
              <p>
                A <strong className="text-[#0A3D62]">RPC Topografia</strong> é uma empresa sediada em 
                Lisboa, especializada em serviços de topografia e georreferenciação para diversos 
                setores da construção e engenharia civil.
              </p>
              <p>
                Com equipamentos de última geração e uma equipa de profissionais qualificados, 
                garantimos rigor técnico e cumprimento de prazos em todos os nossos projetos.
              </p>
              <p>
                A nossa missão é fornecer soluções topográficas precisas que contribuam para o 
                sucesso dos projetos dos nossos clientes, sempre com transparência e profissionalismo.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-3xl font-black text-[#0A3D62]">100+</p>
                <p className="text-sm text-[#2E2E2E]/60 mt-1">Projetos</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-[#0A3D62]">Lisboa</p>
                <p className="text-sm text-[#2E2E2E]/60 mt-1">Localização</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-[#0A3D62]">100%</p>
                <p className="text-sm text-[#2E2E2E]/60 mt-1">Dedicação</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={ABOUT_IMAGE}
                alt="Topógrafo em trabalho de campo"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#3CAB5A] hidden lg:block" />
            <div className="absolute -top-6 -right-6 w-24 h-24 border-2 border-[#0A3D62] hidden lg:block" />
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  const services = [
    {
      icon: Target,
      title: "Levantamentos Topográficos",
      description:
        "Levantamentos de precisão para terrenos, edifícios e infraestruturas. Utilizamos equipamentos GPS e estação total de última geração.",
      image: HERO_IMAGE,
    },
    {
      icon: Grid3X3,
      title: "Loteamentos",
      description:
        "Projetos de loteamento com divisão parcelar, cálculo de áreas e elaboração de plantas topográficas conforme a legislação em vigor.",
      image: SERVICE_IMG_1,
    },
    {
      icon: Building,
      title: "Implantação de Obras",
      description:
        "Marcação e implantação de projetos no terreno com máxima precisão, garantindo a correta execução conforme o projeto de arquitetura.",
      image: SERVICE_IMG_2,
    },
    {
      icon: FileText,
      title: "Cadastro Predial",
      description:
        "Elaboração de plantas cadastrais, atualizações de registos e apoio em processos de regularização de propriedades.",
      image: SERVICE_IMG_1,
    },
  ];

  return (
    <section id="servicos" data-testid="services-section" className="section-padding bg-[#0A3D62]/5">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CAB5A] mb-4">
            Os Nossos Serviços
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A3D62] mb-4">
            Soluções completas de topografia
          </h2>
          <p className="text-base sm:text-lg text-[#2E2E2E]/80 max-w-2xl mx-auto">
            Oferecemos uma gama completa de serviços topográficos para apoiar os seus projetos 
            de construção, engenharia e desenvolvimento imobiliário.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-[#0A3D62]/10">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="service-card bg-[#FDFDF9] p-8 lg:p-12"
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-[#0A3D62] flex items-center justify-center">
                    <Icon className="text-[#FDFDF9]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#0A3D62] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-base leading-relaxed text-[#2E2E2E]/80">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const PortfolioSection = () => {
  const projects = [
    {
      title: "Levantamento Urbanização Cascais",
      category: "Levantamento Topográfico",
      image: HERO_IMAGE,
    },
    {
      title: "Loteamento Parque Industrial",
      category: "Loteamentos",
      image: PORTFOLIO_1,
    },
    {
      title: "Implantação Edifício Comercial",
      category: "Implantação de Obras",
      image: SERVICE_IMG_2,
    },
    {
      title: "Cadastro Zona Histórica",
      category: "Cadastro Predial",
      image: SERVICE_IMG_1,
    },
  ];

  return (
    <section id="projetos" data-testid="portfolio-section" className="section-padding bg-[#FDFDF9]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CAB5A] mb-4">
            Portfólio
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A3D62] mb-4">
            Projetos Realizados
          </h2>
          <p className="text-base sm:text-lg text-[#2E2E2E]/80 max-w-2xl mx-auto">
            Conheça alguns dos trabalhos que realizámos para os nossos clientes. 
            Cada projeto reflete o nosso compromisso com a qualidade e precisão.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <div
              className="portfolio-item relative aspect-[16/9] bg-[#0A3D62]"
              data-testid="portfolio-item-0"
            >
              <img
                src={projects[0].image}
                alt={projects[0].title}
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A3D62] to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3CAB5A] mb-2">
                  {projects[0].category}
                </p>
                <h3 className="text-xl font-semibold text-[#FDFDF9]">
                  {projects[0].title}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-4">
            <div
              className="portfolio-item relative aspect-[4/3] md:aspect-auto md:h-full bg-[#0A3D62]"
              data-testid="portfolio-item-1"
            >
              <img
                src={projects[1].image}
                alt={projects[1].title}
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A3D62] to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3CAB5A] mb-2">
                  {projects[1].category}
                </p>
                <h3 className="text-xl font-semibold text-[#FDFDF9]">
                  {projects[1].title}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-4">
            <div
              className="portfolio-item relative aspect-[4/3] bg-[#0A3D62]"
              data-testid="portfolio-item-2"
            >
              <img
                src={projects[2].image}
                alt={projects[2].title}
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A3D62] to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3CAB5A] mb-2">
                  {projects[2].category}
                </p>
                <h3 className="text-xl font-semibold text-[#FDFDF9]">
                  {projects[2].title}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div
              className="portfolio-item relative aspect-[16/9] bg-[#0A3D62]"
              data-testid="portfolio-item-3"
            >
              <img
                src={projects[3].image}
                alt={projects[3].title}
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A3D62] to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3CAB5A] mb-2">
                  {projects[3].category}
                </p>
                <h3 className="text-xl font-semibold text-[#FDFDF9]">
                  {projects[3].title}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-[#2E2E2E]/60 text-sm italic">
            * Imagens ilustrativas. Portfólio completo disponível mediante solicitação.
          </p>
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://formspree.io/f/mwvwzpwv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        })
      });
      
      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Mensagem enviada com sucesso! Entraremos em contacto brevemente.' });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Erro no envio');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({ type: 'error', message: 'Erro ao enviar mensagem. Por favor, tente novamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contactos" data-testid="contact-section" className="contact-section section-padding">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CAB5A] mb-4">
              Contactos
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#FDFDF9] mb-6">
              Vamos trabalhar juntos?
            </h2>
            <p className="text-base sm:text-lg text-[#FDFDF9]/80 leading-relaxed mb-10">
              Entre em contacto connosco para solicitar um orçamento ou esclarecer qualquer dúvida 
              sobre os nossos serviços. Respondemos em até 24 horas.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#3CAB5A] flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#FDFDF9]" size={20} />
                </div>
                <div>
                  <p className="text-sm text-[#FDFDF9]/60 mb-1">Localização</p>
                  <p className="text-[#FDFDF9] font-medium" data-testid="contact-location">
                    Lisboa, Portugal
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#3CAB5A] flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#FDFDF9]" size={20} />
                </div>
                <div>
                  <p className="text-sm text-[#FDFDF9]/60 mb-1">Telefone</p>
                  <a
                    href="tel:+351914300868"
                    className="text-[#FDFDF9] font-medium hover:text-[#3CAB5A] transition-colors"
                    data-testid="contact-phone"
                  >
                    +351 914 300 868
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#3CAB5A] flex items-center justify-center flex-shrink-0">
                  <Mail className="text-[#FDFDF9]" size={20} />
                </div>
                <div>
                  <p className="text-sm text-[#FDFDF9]/60 mb-1">Email</p>
                  <a
                    href="mailto:renatocarreira@rpc-topografia.com"
                    className="text-[#FDFDF9] font-medium hover:text-[#3CAB5A] transition-colors"
                    data-testid="contact-email"
                  >
                    renatocarreira@rpc-topografia.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FDFDF9] p-8 lg:p-12">
            <h3 className="text-xl font-semibold text-[#0A3D62] mb-6">
              Envie-nos uma mensagem
            </h3>
            
            {submitStatus && (
              <div 
                className={`mb-6 p-4 rounded ${
                  submitStatus.type === 'success' 
                    ? 'bg-[#3CAB5A]/10 text-[#3CAB5A] border border-[#3CAB5A]/20' 
                    : 'bg-red-50 text-red-600 border border-red-200'
                }`}
                data-testid="form-status-message"
              >
                {submitStatus.message}
              </div>
            )}

            <form className="space-y-5" data-testid="contact-form" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                  Nome completo
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#0A3D62]/20 bg-transparent text-[#2E2E2E] placeholder:text-[#2E2E2E]/40 focus:outline-none focus:border-[#3CAB5A] focus:ring-1 focus:ring-[#3CAB5A] transition-colors"
                  placeholder="O seu nome"
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#0A3D62]/20 bg-transparent text-[#2E2E2E] placeholder:text-[#2E2E2E]/40 focus:outline-none focus:border-[#3CAB5A] focus:ring-1 focus:ring-[#3CAB5A] transition-colors"
                  placeholder="seu@email.pt"
                  data-testid="input-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                  Telefone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#0A3D62]/20 bg-transparent text-[#2E2E2E] placeholder:text-[#2E2E2E]/40 focus:outline-none focus:border-[#3CAB5A] focus:ring-1 focus:ring-[#3CAB5A] transition-colors"
                  placeholder="+351 XXX XXX XXX"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                  Mensagem
                </label>
                <textarea
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#0A3D62]/20 bg-transparent text-[#2E2E2E] placeholder:text-[#2E2E2E]/40 focus:outline-none focus:border-[#3CAB5A] focus:ring-1 focus:ring-[#3CAB5A] transition-colors resize-none"
                  placeholder="Descreva o seu projeto ou dúvida..."
                  data-testid="input-message"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full cta-button px-6 py-4 rounded-sm text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    A enviar...
                  </>
                ) : (
                  'Enviar Mensagem'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer data-testid="footer" className="bg-[#2E2E2E] py-12">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="RPC Topografia" className="h-10 w-auto opacity-90" />
            <span className="text-[#FDFDF9] font-bold tracking-tight">RPC Topografia</span>
          </div>
          
          <nav className="flex flex-wrap justify-center gap-6">
            <a href="#inicio" className="text-[#FDFDF9]/60 text-sm hover:text-[#3CAB5A] transition-colors">
              Início
            </a>
            <a href="#sobre" className="text-[#FDFDF9]/60 text-sm hover:text-[#3CAB5A] transition-colors">
              Sobre Nós
            </a>
            <a href="#servicos" className="text-[#FDFDF9]/60 text-sm hover:text-[#3CAB5A] transition-colors">
              Serviços
            </a>
            <a href="#projetos" className="text-[#FDFDF9]/60 text-sm hover:text-[#3CAB5A] transition-colors">
              Projetos
            </a>
            <a href="#contactos" className="text-[#FDFDF9]/60 text-sm hover:text-[#3CAB5A] transition-colors">
              Contactos
            </a>
          </nav>
          
          <p className="text-[#FDFDF9]/40 text-sm">
            © {currentYear} RPC Topografia. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <div className="App">
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <PortfolioSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
